import type { SkillFileDto, SkillPermissionsDto, SkillPreviewDto } from '@dbrain/shared';
import { Badge, Icons, Menu, MenuItem, MenuLabel, RadioCard, Select } from '@dbrain/ui';
import { zipSync } from 'fflate';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationAdmin } from '../../api/plan.js';
import { useCapabilities } from '../../experience.js';
import { useTeams } from '../../api/capabilities.js';
import { useDashboard } from '../../api/hooks.js';
import { formatBytes } from '@dbrain/app-core/format';
import { currentLocale } from '../../i18n/index.js';

/**
 * Skill building blocks (06 §14, D-13, SA-054): what a skill may run, its
 * files, its license, who can use it, and the preview shown before it is
 * installed (docs/design/mockups/SkillEkle.dc.html, SkillEkle2.dc.html).
 */
export type TargetLevel = 'user' | 'workspace' | 'team' | 'organization';

export interface Target {
  level: TargetLevel;
  workspaceId?: string;
  teamId?: string;
}

export const targetBody = (target: Target) => ({
  level: target.level,
  ...(target.level === 'workspace' && target.workspaceId ? { workspaceId: target.workspaceId } : {}),
  ...(target.level === 'team' && target.teamId ? { teamId: target.teamId } : {}),
});

export const estimate = (chars: number) => Math.ceil(chars / 3);

export function PermissionList({ permissions, compact = false }: { permissions: SkillPermissionsDto; compact?: boolean }) {
  const { t } = useTranslation();
  const granted = permissions.commands.length > 0 || permissions.ownScripts;
  return (
    <div className="skill-permissions" data-testid="skill-permissions">
      {granted ? (
        <>
          <p className="skill-permissions__title">{t('skills.permissions.granted')}</p>
          <ul className="skill-permissions__list">
            {permissions.commands.map((command) => (
              <li key={command}>
                <code>{command}</code>
              </li>
            ))}
            {permissions.ownScripts ? <li>{t('skills.permissions.ownScripts')}</li> : null}
          </ul>
          {!compact ? <p className="muted">{t('skills.permissions.scope')}</p> : null}
        </>
      ) : (
        <p className="muted">{t('skills.permissions.none')}</p>
      )}
      {!compact && permissions.ignored.length > 0 ? (
        <>
          <p className="skill-permissions__title">{t('skills.permissions.ignored')}</p>
          <ul className="skill-permissions__list">
            {permissions.ignored.map((item) => (
              <li key={item.entry}>
                <code>{item.entry}</code> — <span className="muted">{t(`skills.ignoredReasons.${item.reason}`, { defaultValue: item.reason })}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

export function FileList({ files }: { files: SkillFileDto[] }) {
  const { t } = useTranslation();
  return (
    <ul className="skill-files" data-testid="skill-files">
      {files.map((file) => (
        <li key={file.path}>
          <code>{file.path}</code> <Badge tone={file.kind === 'script' ? 'warning' : 'neutral'}>{t(`skills.fileKinds.${file.kind}`)}</Badge>{' '}
          <span className="muted">{formatSize(file.size)}</span>
        </li>
      ))}
    </ul>
  );
}

/** a file's size in the interface language's number format: 36,7 KB in Turkish */
export function formatSize(bytes: number): string {
  return formatBytes(bytes, currentLocale());
}

/** Zips a picked folder in the browser (the API takes one file: a ZIP, a SKILL.md or a command file). */
export async function zipFiles(files: { path: string; data: Uint8Array }[]): Promise<Blob> {
  const entries: Record<string, Uint8Array> = {};
  for (const file of files) entries[file.path] = file.data;
  return new Blob([zipSync(entries)], { type: 'application/zip' });
}

export async function zipFolder(files: FileList | File[]): Promise<Blob> {
  return zipFiles(
    await Promise.all(
      Array.from(files).map(async (file) => ({ path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name, data: new Uint8Array(await file.arrayBuffer()) })),
    ),
  );
}

/** The places a user may put a skill: workspaces they edit, teams they lead, the organization (direct for admins, a proposal for members). */
export function useTargets() {
  const dashboard = useDashboard();
  const teams = useTeams();
  // Phase 16a: a person's own account puts skills only in Kişisel and its own workspaces (no Ekip, no Herkes)
  const organization = useCapabilities().organization;
  const admin = useOrganizationAdmin();
  return {
    admin,
    organization,
    workspaces: (dashboard.data?.workspaces ?? []).filter((w) => w.role === 'owner' || w.role === 'editor'),
    teams: (teams.data ?? []).filter((team) => admin || team.myRole === 'lead'),
  };
}

export function useScopeLabel() {
  const { t } = useTranslation();
  const { workspaces, teams } = useTargets();
  return (target: Target) => {
    if (target.level === 'workspace') return workspaces.find((w) => w.id === target.workspaceId)?.name ?? t('skills.levels.workspace');
    if (target.level === 'team') return teams.find((team) => team.id === target.teamId)?.name ?? t('skills.levels.team');
    return t(`skills.scope.${target.level}`);
  };
}

/** "Kimler kullanabilir?": Kişisel, a workspace, a team the user leads, the company (SkillEkle.dc.html). */
export function ScopeCards({ value, onChange, workspaceId }: { value: Target; onChange: (target: Target) => void; workspaceId?: string | undefined }) {
  const { t } = useTranslation();
  const { admin, organization, workspaces, teams } = useTargets();
  return (
    <fieldset className="scope-cards" data-testid="skill-scope">
      <legend className="ui-field__label">{t('skills.scope.label')}</legend>
      <div className={`radio-grid radio-grid--${teams.length > 0 ? 4 : 3}`}>
        <RadioCard name="skill-scope" checked={value.level === 'user'} onChange={() => onChange({ level: 'user' })} title={t('skills.scope.user')} hint={t('skills.scope.userHint')} data-testid="skill-scope-user" />
        <RadioCard
          name="skill-scope"
          checked={value.level === 'workspace'}
          disabled={workspaces.length === 0}
          onChange={() => onChange({ level: 'workspace', workspaceId: workspaceId ?? workspaces[0]?.id ?? '' })}
          title={t('skills.scope.workspace')}
          hint={t('skills.scope.workspaceHint')}
          data-testid="skill-scope-workspace"
        />
        {teams.length > 0 ? (
          <RadioCard
            name="skill-scope"
            checked={value.level === 'team'}
            onChange={() => onChange({ level: 'team', teamId: teams[0]?.id ?? '' })}
            title={t('skills.scope.team')}
            hint={t('skills.scope.teamHint')}
            data-testid="skill-scope-team"
          />
        ) : null}
        {organization ? (
          <RadioCard
            name="skill-scope"
            checked={value.level === 'organization'}
            onChange={() => onChange({ level: 'organization' })}
            title={t('skills.scope.organization')}
            hint={admin ? t('skills.scope.organizationAdminHint') : t('skills.scope.organizationHint')}
            data-testid="skill-scope-organization"
          />
        ) : null}
      </div>
      {value.level === 'workspace' && workspaces.length > 1 ? (
        <label className="scope-cards__pick">
          <span>{t('skills.target.workspace')}</span>
          <Select size="sm" value={value.workspaceId ?? ''} onChange={(e) => onChange({ level: 'workspace', workspaceId: e.target.value })} data-testid="skill-scope-workspace-pick">
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
      {value.level === 'team' && teams.length > 1 ? (
        <label className="scope-cards__pick">
          <span>{t('skills.target.team')}</span>
          <Select size="sm" value={value.teamId ?? ''} onChange={(e) => onChange({ level: 'team', teamId: e.target.value })} data-testid="skill-scope-team-pick">
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
        </label>
      ) : null}
    </fieldset>
  );
}

/** The same choice as a small button ("Kapsam: Kişisel", SkillEkle2.dc.html and SkillYaz.dc.html). */
export function ScopeMenu({ value, onChange, testId }: { value: Target; onChange: (target: Target) => void; testId?: string }) {
  const { t } = useTranslation();
  const { admin, organization, workspaces, teams } = useTargets();
  const label = useScopeLabel();
  const mark = (selected: boolean) => (selected ? <Icons.check /> : <span className="ui-icon" />);
  return (
    <Menu
      align="right"
      className="scope-menu"
      triggerClassName="ui-button ui-button--secondary scope-menu__trigger"
      trigger={
        <>
          <span className="scope-menu__key">{t('skills.scope.short')}</span> {label(value)}
          <Icons.chevronDown size={14} />
        </>
      }
      data-testid={testId ?? 'skill-scope-menu'}
    >
      <MenuItem icon={mark(value.level === 'user')} hint={t('skills.scope.userHint')} onClick={() => onChange({ level: 'user' })} data-testid="scope-user">
        {t('skills.scope.user')}
      </MenuItem>
      {workspaces.length > 0 ? <MenuLabel>{t('skills.scope.workspace')}</MenuLabel> : null}
      {workspaces.map((w) => (
        <MenuItem key={w.id} icon={mark(value.level === 'workspace' && value.workspaceId === w.id)} onClick={() => onChange({ level: 'workspace', workspaceId: w.id })} data-testid="scope-workspace">
          {w.name}
        </MenuItem>
      ))}
      {teams.length > 0 ? <MenuLabel>{t('skills.scope.team')}</MenuLabel> : null}
      {teams.map((team) => (
        <MenuItem key={team.id} icon={mark(value.level === 'team' && value.teamId === team.id)} onClick={() => onChange({ level: 'team', teamId: team.id })}>
          {team.name}
        </MenuItem>
      ))}
      {organization ? (
        <MenuItem icon={mark(value.level === 'organization')} hint={admin ? t('skills.scope.organizationAdminHint') : t('skills.scope.organizationHint')} onClick={() => onChange({ level: 'organization' })} data-testid="scope-organization">
          {t('skills.scope.organization')}
        </MenuItem>
      ) : null}
    </Menu>
  );
}

/** A skill's license as the preview says it: open source, terms to check before sharing, or nothing said. */
export function LicenseLine({ license }: { license: SkillPreviewDto['license'] }) {
  const { t } = useTranslation();
  if (license.open === true) {
    return (
      <span className="license license--open" data-testid="skill-license" data-open="true">
        <Icons.done size={15} />
        {t('skills.license.open', { name: license.name })}
      </span>
    );
  }
  if (license.open === false) {
    return (
      <span className="license license--closed" data-testid="skill-license" data-open="false">
        <Icons.warning size={15} />
        <span>
          {t('skills.license.closed')}
          {license.name ? <span className="license__name"> ({license.name})</span> : null}
        </span>
      </span>
    );
  }
  return (
    <span className="license" data-testid="skill-license" data-open="unknown">
      {t('skills.license.unknown')}
    </span>
  );
}

/**
 * Before installing (D-13): what the skill does, what it may run without
 * asking, what is always asked, what it asked for and was not given (and
 * why), its license, its files.
 */
export function SkillPreviewCard({ preview, onChooseFolder }: { preview: SkillPreviewDto; onChooseFolder?: (folder: string) => void }) {
  const { t } = useTranslation();
  const [showFiles, setShowFiles] = useState(false);
  const scripts = preview.files.filter((f) => f.kind === 'script').length;
  const free = preview.permissions.commands;
  // "its own scripts" only when the package has some; otherwise a note says they are missing
  const ownScripts = preview.permissions.ownScripts && scripts > 0;
  return (
    <section className="skill-preview" aria-label={t('skills.preview.label')} data-testid="skill-preview">
      <div className="skill-preview__head">
        <span className="skill-preview__tile" aria-hidden="true">
          <Icons.skills size={18} />
        </span>
        <span className="skill-preview__titles">
          <span className="skill-preview__name">{preview.name}</span>
          <span className="skill-preview__source">
            {[preview.source, t('skills.add.files', { count: preview.files.length }), scripts > 0 ? t('skills.preview.scripts', { count: scripts }) : null].filter(Boolean).join(' · ')}
          </span>
        </span>
        <button type="button" className="ui-button ui-button--secondary ui-button--sm" aria-expanded={showFiles} onClick={() => setShowFiles(!showFiles)} data-testid="skill-preview-files">
          {t('skills.preview.files')}
          {showFiles ? <Icons.chevronUp size={14} /> : <Icons.chevronDown size={14} />}
        </button>
      </div>
      {showFiles ? <FileList files={preview.files} /> : null}
      <p className="skill-preview__description">{preview.description}</p>
      {preview.candidates.length > 0 && onChooseFolder ? (
        <fieldset className="radio-list skill-preview__choose">
          <legend className="ui-field__label">{t('skills.add.chooseFolder')}</legend>
          {preview.candidates.map((folder) => (
            <RadioCard key={folder} name="skill-folder" onChange={() => onChooseFolder(folder)} title={folder || '/'} data-testid="skill-folder" />
          ))}
        </fieldset>
      ) : null}
      <dl className="skill-preview__facts">
        <dt>{t('skills.preview.free')}</dt>
        <dd>
          {free.length > 0 || ownScripts ? (
            <span className="code-chips">
              {free.map((item) => (
                <code key={item} className="code-chip code-chip--allowed">
                  {item}
                </code>
              ))}
              {ownScripts ? <span className="code-chips__note">{t('skills.permissions.ownScripts')}</span> : null}
            </span>
          ) : (
            t('skills.preview.nothingFree')
          )}
        </dd>
        <dt>{t('skills.preview.asked')}</dt>
        <dd>{t('skills.preview.askedAll')}</dd>
        {preview.permissions.ignored.length > 0 ? (
          <>
            <dt>{t('skills.preview.notGiven')}</dt>
            <dd className="skill-preview__ignored">
              {preview.permissions.ignored.map((item) => (
                <span key={item.entry}>
                  <code className="code-chip code-chip--refused">{item.entry}</code> {t(`skills.ignoredReasons.${item.reason}`, { defaultValue: item.reason })}
                </span>
              ))}
            </dd>
          </>
        ) : null}
        <dt>{t('skills.preview.license')}</dt>
        <dd>
          <LicenseLine license={preview.license} />
        </dd>
      </dl>
      {preview.hooks.length > 0 || preview.warnings.length > 0 ? (
        <ul className="skill-preview__notes">
          {preview.hooks.length > 0 ? <li>{t('skills.hooksIgnored', { hooks: preview.hooks.join(', ') })}</li> : null}
          {preview.warnings
            .filter((w) => w.code !== 'hooks_ignored' && !w.code.startsWith('permission_'))
            .map((warning) => (
              <li key={warning.code}>{t(`skills.warnings.${warning.code}`, { defaultValue: warning.message })}</li>
            ))}
        </ul>
      ) : null}
      <p className="skill-preview__cost">
        {t('skills.cost', { listing: estimate(preview.description.length + preview.name.length + 40), body: preview.bodyTokens })}
        {preview.commit ? ` · ${t('skills.add.commit', { commit: preview.commit.slice(0, 12) })}` : ''}
      </p>
    </section>
  );
}
